// app/treatments/[id]/TreatmentDetail.tsx
"use client";

import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import Link from "next/link";
import Navbar from "../../../components/navbar";
import treatmentsData from "../../data/treatments.json";

const GREEN = "rgb(34 150 83)";

const CLINICS = [
  {
    id: 2,
    name: "Pinak Dental Clinic – Dinnur, RT Nagar",
    address: "Dinnur Main Road, R.T. Nagar",
    city: "Bengaluru, Karnataka - 560032",
    phone: "+918404007002",
    email: "pinakdentalclinicblr@gmail.com",
    mapUrl:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31259.464142049153!2d77.56154000759123!3d13.02374470447953!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae17f70d058539%3A0xcc2306059bf59a20!2sPINAK%20DENTAL%20CLINIC%20ADVANCE%20DENTAL%20CARE%2C%20ROOT%20CANAL%2C%20IMPLANTS%2C%20INVISALIGN%2C%20BRACES%20-%20DINNUR%20ROAD%20BRANCH!5e1!3m2!1sen!2sin!4v1769753007051!5m2!1sen!2sin",
    mapsLink:
      "https://www.google.com/maps/place/PINAK+DENTAL+CLINIC+ADVANCE+DENTAL+CARE,+ROOT+CANAL,+IMPLANTS,+INVISALIGN,+BRACES+-+DINNUR+ROAD+BRANCH/@13.0237282,77.5996434,7178m/data=!3m1!1e3!4m10!3m9!1s0x3bae17f70d058539:0xcc2306059bf59a20!8m2!3d13.0237282!4d77.5996434!16s%2Fg%2F11xlqfvmrv"
  }
];



export default function TreatmentDetail({ treatmentId }: { treatmentId: string }) {
  const treatment = treatmentsData.treatments.find(t => t.id === treatmentId);
  
  const benefitsRef = useRef<HTMLDivElement>(null);
  const procedureRef = useRef<HTMLDivElement>(null);
  const typesRef = useRef<HTMLDivElement>(null);
  const costRef = useRef<HTMLDivElement>(null);
  const faqRef = useRef<HTMLDivElement>(null);
  
  const benefitsInView = useInView(benefitsRef, { once: true, margin: "-10%" });
  const procedureInView = useInView(procedureRef, { once: true, margin: "-10%" });
  const typesInView = useInView(typesRef, { once: true, margin: "-10%" });
  const costInView = useInView(costRef, { once: true, margin: "-10%" });
  const faqInView = useInView(faqRef, { once: true, margin: "-10%" });

  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);

  if (!treatment) {
    return (
      <>
        <Navbar />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-black text-gray-900">Treatment Not Found</h1>
            <Link href="/treatments" className="mt-4 inline-block text-emerald-600 hover:underline">
              ← Back to Treatments
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      
      <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
        {/* Hero Section - ALWAYS VISIBLE */}
        <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-cyan-50 py-20 pt-32">
          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
            <Link href="/treatments" className="group inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
              <motion.span whileHover={{ x: -5 }}>←</motion.span>
              <span className="font-semibold">Back to All Treatments</span>
            </Link>

            <div className="mt-10 grid grid-cols-1 gap-12 lg:grid-cols-2">
              <div>
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-5xl font-black text-gray-900 lg:text-5xl"
                >
                  {treatment.hero.title}
                </motion.h1>
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="mt-4 text-2xl font-semibold text-emerald-600"
                >
                  {treatment.hero.subtitle}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mt-6 text-lg leading-relaxed text-gray-700"
                >
                  {treatment.hero.description}
                </motion.p>

                <div className="mt-8 flex flex-wrap gap-4">
                  <motion.div
                    className="rounded-2xl border-2 border-emerald-200 bg-white p-4"
                    whileHover={{ scale: 1.05, borderColor: GREEN }}
                  >
                    <p className="text-sm font-semibold text-emerald-600">Starting Price</p>
                    <p className="mt-1 text-2xl font-black text-gray-900">{treatment.price}</p>
                  </motion.div>
                  <motion.div
                    className="rounded-2xl border-2 border-emerald-200 bg-white p-4"
                    whileHover={{ scale: 1.05, borderColor: GREEN }}
                  >
                    <p className="text-sm font-semibold text-emerald-600">Duration</p>
                    <p className="mt-1 text-2xl font-black text-gray-900">{treatment.duration}</p>
                  </motion.div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="relative h-[400px] overflow-hidden rounded-3xl border-4 border-emerald-200 shadow-2xl lg:h-[500px]"
              >
                <img
                  src={treatment.hero.image}
                  alt={treatment.name}
                  className="h-full w-full object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* What Is It Section - CONDITIONAL */}
        {treatment.whatIsIt && (
          <section className="py-16">
            <div className="mx-auto max-w-6xl px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                {treatment.whatIsIt.title && (
                  <h2 className="text-4xl font-black text-gray-900">{treatment.whatIsIt.title}</h2>
                )}
                {treatment.whatIsIt.description && (
                  <p className="mt-6 text-lg leading-relaxed text-gray-700">{treatment.whatIsIt.description}</p>
                )}

                {treatment.whatIsIt.componentsTitle && treatment.whatIsIt.components && treatment.whatIsIt.components.length > 0 && (
                  <>
                    <h3 className="mt-12 text-2xl font-bold text-gray-900">{treatment.whatIsIt.componentsTitle}</h3>
                    <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                      {treatment.whatIsIt.components.map((component, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="rounded-2xl border-2 border-emerald-200 bg-white p-6 shadow-md"
                          whileHover={{ y: -5, borderColor: GREEN }}
                        >
                          <h4 className="text-xl font-bold text-gray-900">{component.name}</h4>
                          <p className="mt-3 text-gray-600">{component.description}</p>
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        )}

        {/* Benefits Table - CONDITIONAL */}
        {treatment.benefits?.items && treatment.benefits.items.length > 0 && (
          <section ref={benefitsRef} className="bg-gradient-to-b from-white to-emerald-50/30 py-16">
            <div className="mx-auto max-w-6xl px-6 lg:px-12">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                className="text-center text-4xl font-black text-gray-900"
              >
                {treatment.benefits.title}
              </motion.h2>
              {treatment.benefits.intro && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.1 }}
                  className="mx-auto mt-4 max-w-4xl text-center text-lg text-gray-700"
                >
                  {treatment.benefits.intro}
                </motion.p>
              )}
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={benefitsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="mt-12 overflow-hidden rounded-3xl border-2 border-emerald-200 bg-white shadow-xl"
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-emerald-500">
                      <tr>
                        <th className="px-6 py-4 text-left text-lg font-bold text-white">Benefit</th>
                        <th className="px-6 py-4 text-left text-lg font-bold text-white">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {treatment.benefits.items.map((benefit, index) => (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={benefitsInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="border-b border-emerald-100 transition-colors hover:bg-emerald-50"
                        >
                          <td className="px-6 py-4 font-bold text-gray-900">{benefit.title}</td>
                          <td className="px-6 py-4 text-gray-700">{benefit.description}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Procedure Section - CONDITIONAL */}
        {treatment.procedure?.phases && treatment.procedure.phases.length > 0 && (
          <section ref={procedureRef} className="py-20">
            <div className="mx-auto max-w-6xl px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={procedureInView ? { opacity: 1, y: 0 } : {}}
              >
                {treatment.procedure.title && (
                  <h2 className="text-4xl font-black text-gray-900">{treatment.procedure.title}</h2>
                )}
                {treatment.procedure.intro && (
                  <p className="mt-4 text-lg text-gray-700">{treatment.procedure.intro}</p>
                )}
              </motion.div>

              <div className="mt-16 space-y-12">
                {treatment.procedure.phases.map((phase, phaseIndex) => (
                  <motion.div
                    key={phaseIndex}
                    initial={{ opacity: 0, x: -40 }}
                    animate={procedureInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: phaseIndex * 0.2 }}
                  >
                    <div className="flex items-center gap-4">
                      <div className="grid size-16 flex-shrink-0 place-items-center rounded-2xl bg-emerald-500 text-3xl font-black text-white shadow-lg">
                        {phase.phase}
                      </div>
                      <h3 className="text-2xl font-black text-gray-900">Phase {phase.phase}: {phase.title}</h3>
                    </div>
                    
                    {phase.steps && phase.steps.length > 0 && (
                      <div className="ml-20 mt-6 space-y-4">
                        {phase.steps.map((step, stepIndex) => (
                          <motion.div
                            key={stepIndex}
                            initial={{ opacity: 0, x: -20 }}
                            animate={procedureInView ? { opacity: 1, x: 0 } : {}}
                            transition={{ delay: phaseIndex * 0.2 + stepIndex * 0.1 }}
                            className="rounded-2xl border-2 border-emerald-200 bg-white p-6 shadow-md"
                            whileHover={{ scale: 1.02, borderColor: GREEN }}
                          >
                            <h4 className="text-lg font-bold text-gray-900">{step.name}</h4>
                            <p className="mt-2 text-gray-700">{step.description}</p>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Types Table - CONDITIONAL */}
        {treatment.types?.items && treatment.types.items.length > 0 && (
          <section ref={typesRef} className="bg-gradient-to-b from-emerald-50/30 to-white py-16">
            <div className="mx-auto max-w-6xl px-6 lg:px-12">
              {treatment.types.title && (
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={typesInView ? { opacity: 1, y: 0 } : {}}
                  className="text-center text-4xl font-black text-gray-900"
                >
                  {treatment.types.title}
                </motion.h2>
              )}
              {treatment.types.intro && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={typesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.1 }}
                  className="mx-auto mt-4 max-w-4xl text-center text-lg text-gray-700"
                >
                  {treatment.types.intro}
                </motion.p>
              )}
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={typesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2 }}
                className="mt-12 overflow-hidden rounded-3xl border-2 border-emerald-200 bg-white shadow-xl"
              >
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-emerald-500">
                      <tr>
                        <th className="px-6 py-4 text-left text-lg font-bold text-white">Type of Implant Solution</th>
                        <th className="px-6 py-4 text-left text-lg font-bold text-white">Description</th>
                        <th className="px-6 py-4 text-left text-lg font-bold text-white">Ideal For</th>
                      </tr>
                    </thead>
                    <tbody>
                      {treatment.types.items.map((type, index) => (
                        <motion.tr
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={typesInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="border-b border-emerald-100 transition-colors hover:bg-emerald-50"
                        >
                          <td className="px-6 py-4 font-bold text-gray-900">{type.name}</td>
                          <td className="px-6 py-4 text-gray-700">{type.description}</td>
                          <td className="px-6 py-4 text-gray-700">{type.idealFor}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </div>
          </section>
        )}

        {/* Cost Section - CONDITIONAL */}
        {treatment.cost && treatment.cost.breakdown && treatment.cost.breakdown.length > 0 && (
          <section ref={costRef} className="py-20">
            <div className="mx-auto max-w-6xl px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={costInView ? { opacity: 1, y: 0 } : {}}
              >
                {treatment.cost.title && (
                  <h2 className="text-4xl font-black text-gray-900">{treatment.cost.title}</h2>
                )}
                {treatment.cost.intro && (
                  <p className="mt-6 text-lg leading-relaxed text-gray-700">{treatment.cost.intro}</p>
                )}

                {/* Investment Breakdown */}
                {treatment.cost.breakdownTitle && (
                  <h3 className="mt-12 text-2xl font-bold text-gray-900">{treatment.cost.breakdownTitle}</h3>
                )}
                {treatment.cost.breakdownIntro && (
                  <p className="mt-2 text-gray-700">{treatment.cost.breakdownIntro}</p>
                )}
                
                <div className="mt-8 space-y-6">
                  {treatment.cost.breakdown.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={costInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start justify-between gap-6 rounded-2xl border-2 border-emerald-200 bg-white p-6 shadow-md"
                      whileHover={{ scale: 1.02, borderColor: GREEN }}
                    >
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900">{item.type}</h4>
                        <p className="mt-2 text-gray-600">{item.description}</p>
                      </div>
                      <div className="rounded-xl bg-emerald-50 px-6 py-3">
                        <p className="whitespace-nowrap text-lg font-black text-emerald-600">{item.range}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Key Factors */}
                {treatment.cost.factors && treatment.cost.factors.length > 0 && (
                  <>
                    {treatment.cost.factorsTitle && (
                      <h3 className="mt-16 text-2xl font-bold text-gray-900">{treatment.cost.factorsTitle}</h3>
                    )}
                    {treatment.cost.factorsIntro && (
                      <p className="mt-2 text-gray-700">{treatment.cost.factorsIntro}</p>
                    )}
                    
                    <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                      {treatment.cost.factors.map((factor, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={costInView ? { opacity: 1, y: 0 } : {}}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-white to-emerald-50 p-6"
                          whileHover={{ y: -5, borderColor: GREEN }}
                        >
                          <h4 className="text-lg font-bold text-gray-900">{factor.name}</h4>
                          <p className="mt-2 text-gray-700">{factor.description}</p>
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}

                {/* Additional Costs */}
                {treatment.cost.additional && treatment.cost.additional.length > 0 && (
                  <>
                    {treatment.cost.additionalTitle && (
                      <h3 className="mt-16 text-2xl font-bold text-gray-900">{treatment.cost.additionalTitle}</h3>
                    )}
                    {treatment.cost.additionalIntro && (
                      <p className="mt-2 text-gray-700">{treatment.cost.additionalIntro}</p>
                    )}
                    
                    <div className="mt-8 space-y-4">
                      {treatment.cost.additional.map((item, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={costInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ delay: 0.5 + index * 0.1 }}
                          className="flex items-start justify-between gap-6 rounded-2xl border-2 border-emerald-200 bg-white p-6"
                          whileHover={{ borderColor: GREEN }}
                        >
                          <div>
                            <h4 className="font-bold text-gray-900">{item.name}</h4>
                            {item.description && <p className="mt-1 text-gray-600">{item.description}</p>}
                          </div>
                          {item.range && (
                            <div className="rounded-xl bg-emerald-50 px-4 py-2">
                              <p className="whitespace-nowrap font-bold text-emerald-600">{item.range}</p>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}

                {treatment.cost.cta && (
                  <p className="mt-8 text-lg font-semibold text-gray-800">{treatment.cost.cta}</p>
                )}
              </motion.div>
            </div>
          </section>
        )}

        {/* Candidacy Section - CONDITIONAL */}
        {treatment.candidacy && treatment.candidacy.requirements && treatment.candidacy.requirements.length > 0 && (
          <section className="bg-gradient-to-b from-white to-emerald-50/30 py-16">
            <div className="mx-auto max-w-5xl px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-3xl border-2 border-emerald-200 bg-white p-10 shadow-xl"
              >
                {treatment.candidacy.title && (
                  <h2 className="text-3xl font-black text-gray-900">{treatment.candidacy.title}</h2>
                )}
                {treatment.candidacy.intro && (
                  <p className="mt-4 text-lg text-gray-700">{treatment.candidacy.intro}</p>
                )}
                
                <ul className="mt-6 space-y-3">
                  {treatment.candidacy.requirements.map((req, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <span className="text-2xl text-emerald-600">✓</span>
                      <span className="text-gray-700">{req}</span>
                    </motion.li>
                  ))}
                </ul>

                {treatment.candidacy.cta && (
                  <p className="mt-6 text-lg font-semibold text-gray-800">{treatment.candidacy.cta}</p>
                )}
              </motion.div>
            </div>
          </section>
        )}

        {/* Why Us Section - CONDITIONAL */}
        {treatment.whyUs?.reasons && treatment.whyUs.reasons.length > 0 && (
          <section className="py-16">
            <div className="mx-auto max-w-6xl px-6 lg:px-12">
              {treatment.whyUs.title && (
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-center text-4xl font-black text-gray-900"
                >
                  {treatment.whyUs.title}
                </motion.h2>
              )}
              {treatment.whyUs.intro && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="mx-auto mt-4 max-w-4xl text-center text-lg text-gray-700"
                >
                  {treatment.whyUs.intro}
                </motion.p>
              )}
              
              <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
                {treatment.whyUs.reasons.map((reason, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="rounded-2xl border-2 border-emerald-200 bg-white p-8 text-center shadow-lg"
                    whileHover={{ y: -8, borderColor: GREEN, boxShadow: `0 20px 40px ${GREEN}20` }}
                  >
                    <h3 className="text-xl font-bold text-gray-900">{reason.title}</h3>
                    <p className="mt-4 text-gray-700">{reason.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section - CONDITIONAL */}
        {treatment.faq?.items && treatment.faq.items.length > 0 && (
          <section ref={faqRef} className="bg-gradient-to-b from-emerald-50/30 to-white py-20">
            <div className="mx-auto max-w-4xl px-6 lg:px-12">
              {treatment.faq.title && (
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={faqInView ? { opacity: 1, y: 0 } : {}}
                  className="text-center text-4xl font-black text-gray-900"
                >
                  {treatment.faq.title}
                </motion.h2>
              )}
              
              <div className="mt-16 space-y-4">
                {treatment.faq.items.map((item, index) => {
                  const isExpanded = expandedFaq === index;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={faqInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: index * 0.1 }}
                      className="overflow-hidden rounded-2xl border-2 bg-white shadow-md"
                      style={{ borderColor: isExpanded ? GREEN : "rgba(209, 213, 219, 0.5)" }}
                      whileHover={{ borderColor: GREEN }}
                    >
                      <button
                        onClick={() => setExpandedFaq(isExpanded ? null : index)}
                        className="flex w-full items-start justify-between p-6 text-left"
                      >
                        <h3 className="pr-6 text-lg font-bold text-gray-900">Q: {item.question}</h3>
                        <motion.div
                          animate={{ rotate: isExpanded ? 45 : 0 }}
                          className="grid size-10 flex-shrink-0 place-items-center rounded-full bg-emerald-100 text-2xl font-bold text-emerald-600"
                        >
                          +
                        </motion.div>
                      </button>
                      <motion.div
                        initial={false}
                        animate={{ height: isExpanded ? "auto" : 0 }}
                        className="overflow-hidden"
                      >
                        <p className="border-t border-emerald-100 bg-emerald-50/30 p-6 text-lg leading-relaxed text-gray-700">
                          A: {item.answer}
                        </p>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* CTA Section - ALWAYS VISIBLE */}
        <section className="relative overflow-hidden py-32">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1920&q=80"
              alt="Dental clinic"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/95 via-emerald-500/90 to-emerald-700/95" />
          </div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12">
            <div className="text-center">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-6xl font-black text-white"
              >
                {treatment.cta.title}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mx-auto mt-6 max-w-3xl text-2xl text-white"
              >
                {treatment.cta.subtitle}
              </motion.p>

              {treatment.cta.heading && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="mx-auto mt-8 max-w-2xl text-xl font-semibold text-emerald-100"
                >
                  {treatment.cta.heading}
                </motion.p>
              )}
              {treatment.cta.description && (
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="mx-auto mt-2 text-lg text-emerald-100"
                >
                  {treatment.cta.description}
                </motion.p>
              )}

              {treatment.cta.actions && treatment.cta.actions.length > 0 && (
                <div className="mx-auto mt-8 max-w-3xl">
                  {treatment.cta.actions.map((action, index) => (
                    <motion.p
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      className="mt-2 text-lg text-white"
                    >
                      ✓ {action}
                    </motion.p>
                  ))}
                </div>
              )}

              {/* CTA Buttons */}
              <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                <motion.a
                  href="/#contact"
                  className="inline-flex items-center gap-3 rounded-full bg-white px-12 py-6 text-xl font-bold text-emerald-600 shadow-2xl"
                  whileHover={{ scale: 1.08, boxShadow: "0 30px 60px rgba(0,0,0,0.4)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  📅 Book Consultation
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                  >
                    →
                  </motion.span>
                </motion.a>
                
                <motion.button
                  onClick={() => setShowPhonePopup(true)}
                  className="inline-flex items-center gap-3 rounded-full border-4 border-white bg-transparent px-12 py-6 text-xl font-bold text-white"
                  whileHover={{ scale: 1.08, backgroundColor: "rgba(255,255,255,0.15)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  📞 Call Now
                </motion.button>

                <motion.button
                  onClick={() => setShowWhatsAppPopup(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-green-500 px-12 py-6 text-xl font-bold text-white shadow-2xl"
                  whileHover={{ scale: 1.08, backgroundColor: "#16a34a" }}
                  whileTap={{ scale: 0.98 }}
                >
                  💬 WhatsApp Us
                </motion.button>
              </div>
            </div>

            {/* Clinic Maps */}
            <div className="mt-16 grid grid-cols-1 gap-20 md:grid-cols-2">
              {CLINICS.map((clinic, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="overflow-hidden rounded-3xl border-4 border-white/40 bg-white/20 backdrop-blur-md"
                  whileHover={{ scale: 1.03, borderColor: "rgba(255,255,255,0.8)" }}
                >
                  <div className="relative h-48">
                    <iframe
                      src={clinic.mapUrl}
                      className="h-full w-full"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white">{clinic.name}</h3>
                    <p className="mt-2 text-lg text-emerald-100">{clinic.address}</p>
                    <motion.a
                      href={clinic.mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 text-emerald-200 hover:text-white"
                      whileHover={{ x: 5 }}
                    >
                      <span>→ Get Directions</span>
                    </motion.a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* Phone Popup */}
      <AnimatePresence>
        {showPhonePopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
            onClick={() => setShowPhonePopup(false)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <div className="bg-emerald-500 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white">Call Our Clinics</h3>
                  <button
                    onClick={() => setShowPhonePopup(false)}
                    className="grid size-10 place-items-center rounded-full bg-white/20 text-2xl text-white hover:bg-white/30"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {CLINICS.map((clinic, index) => (
                  <motion.a
                    key={index}
                    href={`tel:${clinic.phone}`}
                    className="mb-4 flex items-center justify-between rounded-2xl border-2 border-emerald-200 p-4 transition-all last:mb-0 hover:border-emerald-500 hover:bg-emerald-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div>
                      <p className="font-bold text-gray-900">{clinic.name}</p>
                      <p className="mt-1 text-lg font-semibold text-emerald-600">{clinic.phone}</p>
                    </div>
                    <span className="text-3xl">📞</span>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp Popup */}
      <AnimatePresence>
        {showWhatsAppPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
            onClick={() => setShowWhatsAppPopup(false)}
          >
            <motion.div
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              <div className="bg-green-500 p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white">Chat on WhatsApp</h3>
                  <button
                    onClick={() => setShowWhatsAppPopup(false)}
                    className="grid size-10 place-items-center rounded-full bg-white/20 text-2xl text-white hover:bg-white/30"
                  >
                    ×
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <p className="mb-6 text-gray-700">Choose a clinic to start chatting:</p>
                {CLINICS.map((clinic, index) => (
                  <motion.a
                    key={index}
                    href={`https://wa.me/${clinic.phone.replace(/\D/g, '')}?text=Hi! I'm interested in ${treatment.name} at ${clinic.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-4 flex items-center justify-between rounded-2xl border-2 border-green-200 p-4 transition-all last:mb-0 hover:border-green-500 hover:bg-green-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div>
                      <p className="font-bold text-gray-900">{clinic.name}</p>
                      <p className="mt-1 text-sm text-gray-600">{clinic.phone}</p>
                    </div>
                    <span className="text-3xl">💬</span>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
